import React, { useState, useCallback, useMemo } from "react";
import {
  Container,
  Box,
  Stack,
  useTheme,
  useMediaQuery,
  Fab,
  Badge,
  Tooltip,
  Zoom,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useInView } from "react-intersection-observer";

// Custom hook
import useDivesites from "../hooks/useDivesites";

// Components
import DivesiteItem from "../components/divesites/DivesiteItem";
import DivesiteSkeleton from "../components/divesites/DivesiteSkeleton";
import ResponsiveFilter from "../components/divesites/FilterDrawer";
import DraggableFab from "../components/DraggableFab";

// Pre-define skeleton array to avoid recreation
const SKELETON_COUNT = 3;
const skeletonArray = Array(SKELETON_COUNT).fill(0);

const Sites = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Custom hook for divesite data and logic
  const {
    divesites,
    loading,
    initialLoading,
    error,
    hasMore,
    isFilterActive,
    filter,
    filterOptions,
    allDivesites,
    loadMore,
    handleFilterChange,
    handleClearFilters,
  } = useDivesites();

  // Local UI state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [fabVisible, setFabVisible] = useState(true);

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    if (!filter) return 0;
    return Object.values(filter).filter((value) =>
      Array.isArray(value) ? value.length > 0 : value
    ).length;
  }, [filter]);

  // Responsive intersection observer settings
  const intersectionConfig = useMemo(
    () => ({
      threshold: isMobile ? 0.05 : 0.1,
      rootMargin: isMobile ? "200px" : "300px",
    }),
    [isMobile]
  );

  // Intersection observer for infinite scrolling
  const { ref, inView } = useInView(intersectionConfig);

  // Effect to trigger loadMore when reaching bottom of page
  React.useEffect(() => {
    if (inView) {
      loadMore();
    }
  }, [inView, loadMore]);

  // Hide FAB when scrolling down, show when scrolling up
  React.useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setFabVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 200) {
        setFabVisible(false);
      }

      lastScrollY = currentScrollY;
    };

    if (isMobile) {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isMobile]);

  // Memoized handlers
  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  // Responsive skeleton count
  const responsiveSkeletonCount = useMemo(() => {
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 4;
  }, [isMobile, isTablet]);

  // Create skeleton placeholders for initial loading
  const renderSkeletons = useMemo(
    () =>
      Array(responsiveSkeletonCount)
        .fill(0)
        .map((_, index) => <DivesiteSkeleton key={`skeleton-${index}`} />),
    [responsiveSkeletonCount]
  );

  // Responsive container and spacing
  const containerProps = useMemo(
    () => ({
      maxWidth: "lg",
      sx: {
        pb: isMobile ? 12 : 4,
        px: isMobile ? 2 : 3,
      },
    }),
    [isMobile]
  );

  const stackSpacing = useMemo(() => (isMobile ? 1.5 : 2), [isMobile]);

  // Style constants with responsive adjustments
  const centerBoxSx = useMemo(
    () => ({
      textAlign: "center",
      p: isMobile ? 2 : 4,
      fontSize: isMobile ? "0.9rem" : "1rem",
    }),
    [isMobile]
  );

  const scrollMoreSx = useMemo(
    () => ({
      textAlign: "center",
      p: isMobile ? 1 : 2,
      opacity: 0.7,
      fontSize: isMobile ? "0.8rem" : "0.9rem",
    }),
    [isMobile]
  );

  // Optimize divesite rendering
  const contentToDisplay = useMemo(() => {
    if (initialLoading) {
      return renderSkeletons;
    }

    if (divesites.length > 0) {
      return divesites.map((divesite, index) => {
        const isLast = !isFilterActive && index === divesites.length - 1;
        return (
          <DivesiteItem
            key={divesite._id || index}
            divesite={divesite}
            isLast={isLast}
            refProp={isLast ? ref : null}
            isMobile={isMobile}
          />
        );
      });
    }

    return (
      <Box sx={centerBoxSx}>
        {error ? (
          <Box
            sx={{
              color: "error.main",
              p: 2,
              borderRadius: 1,
              bgcolor: "error.light",
              color: "error.contrastText",
            }}
          >
            Error: {error}
          </Box>
        ) : (
          <Box sx={{ color: "text.secondary" }}>
            {isFilterActive
              ? "No dive sites match your filters"
              : "No dive sites available"}
          </Box>
        )}
      </Box>
    );
  }, [
    initialLoading,
    divesites,
    error,
    isFilterActive,
    renderSkeletons,
    ref,
    centerBoxSx,
    isMobile,
  ]);

  return (
    <Container {...containerProps}>
      {/* Single FAB implementation for both mobile and desktop */}
      {isMobile ? (
        <Fab
          color={isFilterActive ? "secondary" : "primary"}
          onClick={handleDrawerOpen}
          size="large"
          aria-label="open filters"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
            transform: fabVisible ? "translateY(0)" : "translateY(100px)",
            transition: "transform 0.2s ease-in-out",
            "@media (max-height: 600px)": {
              bottom: 16,
              right: 16,
            },
          }}
        >
          <FilterListIcon />
        </Fab>
      ) : (
        <DraggableFab
          onClick={handleDrawerOpen}
          color={isFilterActive ? "secondary" : "primary"}
          initialPosition={{ bottom: 20, right: 20 }}
          tooltipTitle="Filters"
        >
          Filters
        </DraggableFab>
      )}

      {/* Filter Drawer */}
      <ResponsiveFilter
        initialLoading={initialLoading}
        uniqueDiveTypes={filterOptions?.uniqueDiveTypes || []}
        uniqueCities={filterOptions?.uniqueCities || []}
        uniqueStates={filterOptions?.uniqueStates || []}
        filter={filter}
        handleFilterChange={handleFilterChange}
        handleClearFilters={handleClearFilters}
        isFilterActive={isFilterActive}
        allDivesites={allDivesites}
        displayDivesites={divesites}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerClose}
      />

      <Stack spacing={stackSpacing}>
        {contentToDisplay}

        {/* Loading indicator for pagination */}
        {loading && !initialLoading && <DivesiteSkeleton isMobile={isMobile} />}

        {/* Scroll for more indicator */}
        {hasMore && !loading && !isFilterActive && divesites.length > 0 && (
          <Box sx={scrollMoreSx}>
            {isMobile ? "Scroll for more" : "Scroll for more dive sites"}
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default React.memo(Sites);
